import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  Alert,
  Autocomplete,
} from "@mui/material";
import { listKeys } from "../../api/agentApi";

interface WebhookConfigBuilderProps {
  value: {
    adapter_type: string;
    secrets: Record<string, string>;
  };
  onChange: (config: { adapter_type: string; secrets: Record<string, string> }) => void;
  error?: string;
  showValidation?: boolean;
  readonly?: boolean;
}

type AdapterType =
  | "slack_keymanager"
  | "zendesk_apikey"
  | "zendesk_bearer"
  | "zendesk_basic"
  | "generic_apikey"
  | "generic_bearer";

interface AdapterOption {
  value: AdapterType;
  label: string;
  adapter: string; // The actual adapter name for the backend
  secretFields: {
    name: string;
    label: string;
    type: "text" | "password";
    placeholder: string;
  }[];
}

const ADAPTER_OPTIONS: AdapterOption[] = [
  {
    value: "slack_keymanager",
    label: "Slack with Key Manager",
    adapter: "slack",
    secretFields: [
      {
        name: "signing_secret",
        label: "Signing Secret",
        type: "password",
        placeholder: "Enter Slack signing secret",
      },
    ],
  },
  {
    value: "zendesk_apikey",
    label: "Zendesk with X-API-Key",
    adapter: "zendesk",
    secretFields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "Enter Zendesk API key",
      },
    ],
  },
  {
    value: "zendesk_bearer",
    label: "Zendesk with Bearer Token",
    adapter: "zendesk",
    secretFields: [
      {
        name: "webhook_token",
        label: "Webhook Token",
        type: "password",
        placeholder: "Enter Zendesk webhook token",
      },
    ],
  },
  {
    value: "zendesk_basic",
    label: "Zendesk with Basic Auth",
    adapter: "zendesk",
    secretFields: [
      {
        name: "username",
        label: "Username",
        type: "text",
        placeholder: "Enter username",
      },
      {
        name: "password",
        label: "Password",
        type: "password",
        placeholder: "Enter password",
      },
    ],
  },
  {
    value: "generic_apikey",
    label: "Generic with X-API-Key",
    adapter: "generic",
    secretFields: [
      {
        name: "api_key",
        label: "API Key",
        type: "password",
        placeholder: "Enter API key",
      },
    ],
  },
  {
    value: "generic_bearer",
    label: "Generic with Bearer Token",
    adapter: "generic",
    secretFields: [
      {
        name: "webhook_token",
        label: "Webhook Token",
        type: "password",
        placeholder: "Enter bearer token",
      },
    ],
  },
];

/**
 * Validate webhook configuration
 */
export const validateWebhookConfig = (config: {
  adapter_type: string;
  secrets: Record<string, string>;
}): { valid: boolean; error?: string } => {
  if (!config.adapter_type) {
    return { valid: false, error: "Adapter type is required" };
  }

  const adapter = ADAPTER_OPTIONS.find((opt) => opt.adapter === config.adapter_type);
  if (!adapter) {
    return { valid: false, error: "Invalid adapter type" };
  }

  // Check all required secret fields are provided and have key: prefix
  for (const field of adapter.secretFields) {
    const value = config.secrets[field.name];
    if (!value || typeof value !== "string" || value.trim() === "") {
      return { valid: false, error: `${field.label} key is required` };
    }
  }

  return { valid: true };
};

/**
 * Generate a webhook label based on adapter type
 */
export const getWebhookLabel = (adapterType: string): string => {
  const adapter = ADAPTER_OPTIONS.find((opt) => opt.adapter === adapterType);
  return adapter ? adapter.label : "Webhook Trigger";
};

export const WebhookConfigBuilder: React.FC<WebhookConfigBuilderProps> = ({
  value,
  onChange,
  error,
  showValidation = false,
  readonly = false,
}) => {
  const [selectedAdapter, setSelectedAdapter] = useState<AdapterType | "">(""); 
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [availableKeys, setAvailableKeys] = useState<any[]>([]);
  const [keysLoading, setKeysLoading] = useState(false);

  // Fetch available keys from Key Manager
  useEffect(() => {
    async function fetchKeys() {
      setKeysLoading(true);
      try {
        const res = await listKeys();
        setAvailableKeys(res.keys || []);
      } catch (e) {
        console.error("Failed to fetch keys:", e);
        setAvailableKeys([]);
      } finally {
        setKeysLoading(false);
      }
    }
    fetchKeys();
  }, []);

  // Initialize from value prop
  useEffect(() => {
    if (value && value.adapter_type) {
      // Find the adapter option that matches the backend adapter type
      const matchingOption = ADAPTER_OPTIONS.find(
        (opt) => opt.adapter === value.adapter_type
      );
      if (matchingOption) {
        setSelectedAdapter(matchingOption.value);
        setSecrets(value.secrets || {});
      }
      setIsInitialized(true);
    } else {
      // Default initialization
      setIsInitialized(true);
    }
  }, [value]);

  // Update parent when values change
  useEffect(() => {
    if (!isInitialized) return;
    // Don't propagate if no adapter is selected yet
    if (!selectedAdapter) return;

    const currentOption = ADAPTER_OPTIONS.find((opt) => opt.value === selectedAdapter);
    if (!currentOption) return;

    const newConfig = {
      adapter_type: currentOption.adapter,
      secrets,
    };

    // Only call onChange if value actually changed
    if (
      JSON.stringify(newConfig) !==
      JSON.stringify({ adapter_type: value?.adapter_type, secrets: value?.secrets })
    ) {
      onChange(newConfig);
    }
  }, [selectedAdapter, secrets, isInitialized, onChange, value]);

  const handleAdapterChange = (newAdapter: AdapterType | "") => {
    setSelectedAdapter(newAdapter);
    // Reset secrets when adapter changes
    setSecrets({});
  };

  const handleSecretChange = (fieldName: string, fieldValue: string) => {
    setSecrets((prev) => ({
      ...prev,
      [fieldName]: fieldValue,
    }));
  };

  const currentOption = ADAPTER_OPTIONS.find((opt) => opt.value === selectedAdapter);
  const validation = validateWebhookConfig({
    adapter_type: currentOption?.adapter || "",
    secrets,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Adapter Type Selector */}
      <FormControl fullWidth size="small" variant="outlined">
        <InputLabel shrink sx={{ fontSize: "13px", fontWeight: 600, bgcolor: "white", px: 0.5 }}>Adapter Type</InputLabel>
        <Select
          value={selectedAdapter}
          label="Adapter Type"
          displayEmpty
          notched
          onChange={(e) => handleAdapterChange(e.target.value as AdapterType | "")}
          sx={{ fontSize: "13px" }}
        >
          <MenuItem value="" sx={{ fontSize: "13px", color: "#94A3B8" }}>
            <em>Select adapter…</em>
          </MenuItem>
          {ADAPTER_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ fontSize: "13px" }}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Secret Fields - Key Manager Selection */}
      {currentOption?.secretFields.map((field) => {
        // Extract key_id from "key:<uuid>" format
        const currentRef = typeof secrets[field.name] === "string" ? secrets[field.name] : "";
        const currentKeyId = currentRef.startsWith("key:") ? currentRef.slice(4) : "";
        const selectedKey = availableKeys.find((k: any) => k.id === currentKeyId) || null;

        return (
          <Autocomplete
            key={field.name}
            size="small"
            options={availableKeys}
            getOptionLabel={(option: any) => option.name || option.id || ""}
            value={selectedKey}
            loading={keysLoading}
            disabled={readonly}
            onChange={(_e: any, newValue: any) => {
              if (newValue) {
                handleSecretChange(field.name, `key:${newValue.id}`);
              } else {
                handleSecretChange(field.name, "");
              }
            }}
            isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${field.label} (Key)`}
                placeholder={`Select a key for ${field.label.toLowerCase()}...`}
                error={showValidation && !secrets[field.name]}
                InputLabelProps={{ ...params.InputLabelProps, sx: { fontSize: "11px", fontWeight: 700 } }}
                inputProps={{ ...params.inputProps, style: { fontSize: "12px" } }}
              />
            )}
            renderOption={(props, option: any) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{option.name}</Typography>
                  {option.provider && (
                    <Typography sx={{ fontSize: '10px', color: '#94A3B8' }}>{option.provider}</Typography>
                  )}
                </Box>
              </li>
            )}
          />
        );
      })}

      {/* Preview */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: "#F8FAFC",
          borderRadius: "6px",
          border: "1px solid #E2E8F0",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "#64748B",
            fontSize: "10px",
            fontWeight: 700,
            display: "block",
            mb: 0.5,
          }}
        >
          Webhook Preview
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={currentOption?.label || "Webhook Trigger"}
            size="small"
            sx={{ fontSize: "11px", fontWeight: 600 }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "#94A3B8",
            fontSize: "10px",
            display: "block",
            mt: 0.5,
            fontFamily: "monospace",
          }}
        >
          Adapter: {currentOption?.adapter || "none"}
          {currentOption?.secretFields.map((field) => {
            const currentRef = typeof secrets[field.name] === "string" ? secrets[field.name] : "";
            const currentKeyId = currentRef.startsWith("key:") ? currentRef.slice(4) : "";
            const selectedKey = availableKeys.find((k: any) => k.id === currentKeyId);
            return selectedKey ? (
              <span key={field.name}> | {field.label}: {selectedKey.name}</span>
            ) : null;
          })}
        </Typography>
      </Box>

      {/* Validation Error */}
      {showValidation && (error || !validation.valid) && (
        <Alert severity="error" sx={{ fontSize: "11px", py: 0.5 }}>
          {error || validation.error}
        </Alert>
      )}
    </Box>
  );
};
